import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Eye, EyeOff, AlertCircle, CheckCircle, Check } from 'lucide-react';
import { Trans } from 'react-i18next';
import { useTranslation } from '@parentpath/i18n';
import { useAuth } from '../contexts/AuthContext';
import { createRegisterSchema, type RegisterFormData } from '../schemas/authSchemas';

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, isLoading: authLoading } = useAuth();
  const { t } = useTranslation('auth');
  const registerSchema = useMemo(() => createRegisterSchema(t), [t]);

  // Get redirect path from URL (e.g., /register?redirect=/invite/xxx)
  const redirectPath = searchParams.get('redirect');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationNotice, setShowVerificationNotice] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false,
      acceptHealthDataConsent: false,
    },
  });

  // Watch password for real-time validation feedback
  const password = watch('password', '');

  // Password requirements validation
  const passwordRequirements = [
    { label: t('register.req8Chars'), met: password.length >= 8 },
    { label: t('register.reqUppercase'), met: /[A-Z]/.test(password) },
    { label: t('register.reqLowercase'), met: /[a-z]/.test(password) },
    { label: t('register.reqNumber'), met: /\d/.test(password) },
  ];

  const onSubmit = async (data: RegisterFormData) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const { error } = await signUp(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        data.acceptTerms,
        data.acceptHealthDataConsent,
        redirectPath || undefined
      );

      if (error) {
        if (error.message.includes('already registered')) {
          setError(t('register.emailAlreadyRegistered'));
        } else {
          setError(error.message);
        }
        return;
      }

      // Show verification notice
      setShowVerificationNotice(true);
    } catch {
      setError(t('common.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }

  // Show verification notice after successful registration
  if (showVerificationNotice) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 py-12">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('register.almostDone')}
              </h2>
              <p className="text-white/70 mb-6">
                {t('register.verificationEmailSent')}
              </p>
              <div className="bg-pp-orange/20 border border-pp-orange/30 rounded-[10px] p-4 mb-6">
                <p className="text-sm text-white/80">
                  <strong className="text-white">{t('register.spamTipPrefix')}</strong> {t('register.spamTipText')}
                </p>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-3 glass-effect border border-white/25 text-white font-medium rounded-[10px] hover:bg-white/10 transition-colors"
              >
                {t('register.backToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center bg-pp-gradient px-4 py-12">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="https://parentpath.de/pp_logo-piktogram-w100.svg"
            alt="ParentPath"
            className="w-16 h-16"
          />
        </div>

        <h1 className="text-center text-2xl font-bold text-white">
          {t('register.title')}
        </h1>
        <p className="mt-2 text-center text-sm text-white/70">
          {t('register.subtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-[10px] flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Name Fields - Side by Side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">
                  {t('register.firstName')}
                </label>
                <input
                  {...register('firstName')}
                  type="text"
                  placeholder={t('register.firstNamePlaceholder')}
                  autoComplete="given-name"
                  className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                {errors.firstName && (
                  <p className="mt-1.5 text-sm text-red-400">{errors.firstName.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">
                  {t('register.lastName')}
                </label>
                <input
                  {...register('lastName')}
                  type="text"
                  placeholder={t('register.lastNamePlaceholder')}
                  autoComplete="family-name"
                  className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                {errors.lastName && (
                  <p className="mt-1.5 text-sm text-red-400">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('common.emailLabel')}
              </label>
              <div className="relative">
                <input
                  {...register('email')}
                  type="email"
                  placeholder={t('common.emailPlaceholder')}
                  autoComplete="email"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('common.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('register.passwordPlaceholder')}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('register.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1.5 text-sm text-red-400">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Password Requirements */}
            <div className="text-xs space-y-1.5">
              <p className="text-white/50">{t('register.passwordRequirements')}</p>
              <ul className="space-y-1 pl-1">
                {passwordRequirements.map((req, index) => (
                  <li
                    key={index}
                    className={`flex items-center gap-2 transition-colors ${
                      req.met ? 'text-green-400' : 'text-white/50'
                    }`}
                  >
                    {req.met ? (
                      <Check className="w-3.5 h-3.5 flex-shrink-0" />
                    ) : (
                      <span className="w-3.5 h-3.5 flex-shrink-0 flex items-center justify-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      </span>
                    )}
                    {req.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Terms & Privacy Checkbox */}
            <div className="space-y-1">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('acceptTerms')}
                  className="mt-1 h-4 w-4 text-pp-orange bg-white/10 border-white/20 rounded focus:ring-pp-orange/50"
                />
                <span className="text-sm text-white/70">
                  <Trans
                    i18nKey="register.acceptTermsLabel"
                    ns="auth"
                    components={{
                      termsLink: <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-pp-orange hover:text-pp-orange/80 underline" />,
                      privacyLink: <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-pp-orange hover:text-pp-orange/80 underline" />
                    }}
                  />
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="text-sm text-red-400">{errors.acceptTerms.message}</p>
              )}
            </div>

            {/* Health Data Consent Checkbox (GDPR Art. 9) */}
            <div className="space-y-1">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('acceptHealthDataConsent')}
                  className="mt-1 h-4 w-4 text-pp-orange bg-white/10 border-white/20 rounded focus:ring-pp-orange/50"
                />
                <span className="text-sm text-white/70">
                  <Trans
                    i18nKey="register.healthConsentLabel"
                    ns="auth"
                    components={{
                      privacyLink: <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-pp-orange hover:text-pp-orange/80 underline" />
                    }}
                  />
                </span>
              </label>
              <p className="text-xs text-white/50 ml-7">
                {t('register.consentRevocable')}
              </p>
              {errors.acceptHealthDataConsent && (
                <p className="text-sm text-red-400">{errors.acceptHealthDataConsent.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold
                         hover:bg-pp-orange/90 transition-colors disabled:opacity-50
                         flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('register.title')
              )}
            </button>
          </form>

          {/* Login Link */}
          <p className="mt-6 text-center text-sm text-white/70">
            {t('register.haveAccount')}{' '}
            <Link
              to="/login"
              className="font-medium text-pp-orange hover:text-pp-orange/80"
            >
              {t('register.signInNow')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
